// ── TWA (Trusted Web Activity) detection ─────────────────────────────────────
// Returns true when the app is running inside the Android Play Store TWA wrapper.
// In that context, Stripe checkout must open in an external Chrome window instead
// of navigating inside the TWA — to comply with Google Play payment policy.
//
// Detection strategy:
//   1. On first load, document.referrer contains 'android-app://' — persist to
//      sessionStorage so all subsequent React Router navigations still detect TWA.
//   2. Read sessionStorage on every call so the flag survives in-app navigation.

import { isNative } from './capacitor';

const TWA_KEY = 'dbt_twa';

// Call once at app startup (App.js) to capture the referrer before it changes.
export const initTWADetection = () => {
  try {
    if (document.referrer.includes('android-app://')) {
      sessionStorage.setItem(TWA_KEY, '1');
    }
  } catch {
    // sessionStorage unavailable — silently ignore
  }
};

export const isTWA = () => {
  try {
    // Check live referrer (works on first load) OR persisted flag (works after nav)
    return (
      document.referrer.includes('android-app://') ||
      sessionStorage.getItem(TWA_KEY) === '1'
    );
  } catch {
    return false;
  }
};

// Open a payment/external URL correctly depending on context:
//   - TWA: opens in external Chrome browser (Google Play payment policy compliant)
//   - Web: navigates in the same tab (existing behaviour, unchanged)
export const openCheckoutUrl = (url) => {
  if (isTWA()) {
    window.open(url, '_blank', 'noopener');
  } else {
    window.location.href = url;
  }
};

// Open an external URL from anywhere, including inside the native apps.
//
// Capacitor implements no `onCreateWindow`, so `window.open(url, '_blank')` is a
// SILENT NO-OP in the WebView — buttons using it (Manage Subscription, Open
// Stripe Dashboard) simply did nothing on iOS/Android. A plain navigation is
// intercepted by Capacitor's shouldOverrideUrlLoading and handed to the OS
// (ACTION_VIEW / Safari), which opens the store or browser and leaves the app
// exactly where it was. On the web a new tab is still the right behaviour.
export const openExternalUrl = (url) => {
  try {
    if (isNative()) {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (_) {
    try { window.location.href = url; } catch (__) { /* nothing more we can do */ }
  }
};
